import { Button, Flex, Stack, Switch, Text } from '@mantine/core';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AdditionalInstructionsField } from 'src/meeting-note/components/AdditionalInstructionsField';
import { TemplateCard } from './TemplateCard';

type Props = {
  templates: TemplateWithoutSections[];
  selectedTemplateId?: string;
  onSelectTemplate: (templateId: string) => void;
  onGenerate: () => void;
  isGenerating?: boolean;
  onBack?: () => void;
  summarise?: boolean;
  onSummariseChange?: (value: boolean) => void;
  showSummariseToggle?: boolean;
  additionalInstructions?: string;
  onAdditionalInstructionsChange?: (value: string) => void;
};

export const TemplateSelection = ({
  templates,
  selectedTemplateId,
  onSelectTemplate,
  onGenerate,
  isGenerating,
  onBack,
  summarise,
  onSummariseChange,
  showSummariseToggle,
  additionalInstructions,
  onAdditionalInstructionsChange,
}: Props) => {
  const selectedTemplate = templates.find(template => template.id === selectedTemplateId);

  return (
    <Stack gap="xs">
      <Flex justify="space-between" align="center">
        <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Select template to generate a meeting note
        </Text>
        {onBack && (
          <Text
            size="textSm"
            fw={FONT_WEIGHTS.semibold}
            c="brand.6"
            style={{ cursor: 'pointer' }}
            onClick={onBack}
          >
            Back
          </Text>
        )}
      </Flex>
      {showSummariseToggle && (
        <Flex justify="space-between" align="center">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
            Summarise meeting note
          </Text>
          <Switch
            checked={summarise ?? true}
            onChange={event => onSummariseChange?.(event.currentTarget.checked)}
          />
        </Flex>
      )}
      <Stack gap="xxs">
        {templates.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            selected={template.id === selectedTemplateId}
            onSelect={() => onSelectTemplate(template.id)}
          />
        ))}
      </Stack>
      {selectedTemplate && (
        <Stack gap="sm" mt="xs">
          <AdditionalInstructionsField
            templateId={selectedTemplate.id}
            eligibleForSummarisation={selectedTemplate.eligibleForSummarisation ?? true}
            value={additionalInstructions ?? ''}
            onChange={value => onAdditionalInstructionsChange?.(value)}
          />
          <Button onClick={onGenerate} loading={isGenerating} fullWidth>
            Generate meeting note
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
